import type { AppStore } from "@/store/AppStore";
import type {
  AiToolkitAssistantTask,
  SearchAssistantTasksInput,
  SearchAssistantTasksOutput,
} from "@mem-client/ai-toolkit/contract";
import {
  AiToolkitToolName,
  searchAssistantTasksInputSchema,
  searchAssistantTasksOutputSchema,
} from "@mem-client/ai-toolkit/contract";
import type { AiToolkitToolDefinition } from "@/modules/ai-toolkit/tools/types";
import {
  compareIsoStringsDescending,
  DEFAULT_AI_TOOLKIT_SEARCH_LIMIT,
  matchesAiToolkitSearchQuery,
  normalizeAiToolkitFilterValue,
  normalizeAiToolkitSearchQuery,
} from "@/modules/ai-toolkit/tools/utils/local-search-helpers";
import {
  buildAssistantTaskFromCreateChangeRequestUpdateValue,
  buildAssistantTaskFromSyncUpdateValue,
  isCreateTaskChangeRequestUpdateValue,
} from "@/modules/ai-toolkit/tools/assistant-tasks/assistant-task-tools.helpers";

const matchesSearchInput = ({
  task,
  input,
  query,
}: {
  task: AiToolkitAssistantTask;
  input: SearchAssistantTasksInput;
  query: string | undefined;
}): boolean => {
  const assistantId = normalizeAiToolkitFilterValue(input.assistantId);
  const projectId = normalizeAiToolkitFilterValue(input.projectId);
  const status = normalizeAiToolkitFilterValue(input.status);

  if (assistantId && task.assistantId !== assistantId) return false;
  if (projectId && task.projectId !== projectId) return false;
  if (status && task.status !== status) return false;

  return matchesAiToolkitSearchQuery({
    query,
    values: [task.title, task.description],
  });
};

export const searchAssistantTasks = async ({
  store,
  input,
}: {
  store: AppStore;
  input: SearchAssistantTasksInput;
}): Promise<SearchAssistantTasksOutput> => {
  const query = normalizeAiToolkitSearchQuery(input.query);
  const [taskRows, changeRequestRows] = await Promise.all([
    store.assistantTasks.localTable.toArray(),
    store.assistantTaskChangeRequests.localTable.toArray(),
  ]);
  const realTaskIds = new Set(taskRows.map(taskRow => taskRow.model_id));

  const tasks = [
    ...taskRows.map(taskRow => buildAssistantTaskFromSyncUpdateValue({ updateValue: taskRow })),
    ...changeRequestRows
      .filter(changeRequestRow => !realTaskIds.has(changeRequestRow.model_id))
      .filter(changeRequestRow =>
        isCreateTaskChangeRequestUpdateValue({ updateValue: changeRequestRow })
      )
      .map(changeRequestRow =>
        buildAssistantTaskFromCreateChangeRequestUpdateValue({ updateValue: changeRequestRow })
      ),
  ]
    .filter(task => matchesSearchInput({ task, input, query }))
    .sort(
      (left, right) =>
        compareIsoStringsDescending({
          left: left.updatedAt,
          right: right.updatedAt,
        }) || left.id.localeCompare(right.id)
    )
    .slice(0, input.limit ?? DEFAULT_AI_TOOLKIT_SEARCH_LIMIT);

  return { tasks };
};

export const searchAssistantTasksTool: AiToolkitToolDefinition<
  AiToolkitToolName.SearchAssistantTasks,
  SearchAssistantTasksInput,
  SearchAssistantTasksOutput
> = {
  name: AiToolkitToolName.SearchAssistantTasks,
  inputSchema: searchAssistantTasksInputSchema,
  outputSchema: searchAssistantTasksOutputSchema,
  strict: true,
  execute: searchAssistantTasks,
};
